import {useIntl} from 'react-intl';
import {connect} from 'react-redux';
import React, {useEffect, useMemo} from 'react';

import {RootState} from '../../../store';
import {getProjectData} from '../../../store/project';
import {Project} from '../../../types/project/Project';
import {AnalyticsParameters} from './AnalyticsParameters';
import {toast} from '../../../base-components/StudioToast';
import {EnvironmentParameters} from './EnvironmentParameters';
import {useTestListByLatest} from '../../../api/test/TestList';
import {
  useDeploy,
  setDeployConfig,
  useDeploymentDetails,
  selectIsTaskInProgress,
} from '../useDeploy';
import {
  useDeploymentAnalyticParameters,
  useDeploymentDefaultEnvironmentParameters as useAnalyticsParameters,
} from '../../../api/deployment/DeploymentAnalyticsParameters';
import {
  useDeploymentEnvironmentParameters,
  useDeploymentDefaultEnvironmentParameters as useEnvironmentParameters,
} from '../../../api/deployment/DeploymentEnvironmentParameters';

import './DeploySettings.scss';

type DeploySettingsProps = {
  openProjectData: Project;
};

export function DeploySettingsBase({openProjectData}: DeploySettingsProps) {
  const intl = useIntl();

  const [deployState, dispatch] = useDeploy();
  const {deployConfig, selectedDeploymentIds} = useMemo(() => deployState, [deployState]);
  const isTaskInProgress = useMemo(() => selectIsTaskInProgress(deployState), [
    deployState,
  ]);

  const testSelectedId = useMemo(
    () => (selectedDeploymentIds?.length > 1 ? void 0 : selectedDeploymentIds?.[0]),
    [selectedDeploymentIds]
  );
  const testSelectedDeployment = useDeploymentDetails(testSelectedId);

  const selectedApplication = useMemo(() => {
    let selectedApplication;

    if (openProjectData?.applications) {
      selectedApplication =
        openProjectData?.applications?.[
          testSelectedDeployment?.applicationId || deployConfig?.deploymentId
        ];
    }

    return selectedApplication;
  }, [
    deployConfig?.deploymentId,
    openProjectData?.applications,
    testSelectedDeployment?.applicationId,
  ]);

  const [
    previousSelectedApplicationId,
    setPreviousSelectedApplicationId,
  ] = React.useState<string | undefined>();
  useEffect(
    function resetSettingsWhenApplicationChanges() {
      if (
        selectedApplication &&
        selectedApplication.id !== previousSelectedApplicationId
      ) {
        setPreviousSelectedApplicationId(selectedApplication.id);
        dispatch(
          setDeployConfig({
            environment: {},
            analyticsParameters: [],
          })
        );
      }
    },
    [selectedApplication, previousSelectedApplicationId, dispatch]
  );

  const isItAnAnalyticsApplication = useMemo(
    () => selectedApplication?.analyticsApplication,
    [selectedApplication]
  );

  const {data: historyItems} = useTestListByLatest({
    projectId: openProjectData.id,
    avoidFetch: !isItAnAnalyticsApplication,
  });

  const {
    data: defaultEnvironmentParameters = {},
    error: errorFetchingDefaultEnvironmentParameters,
  } = useEnvironmentParameters({
    projectId: openProjectData.id,
    applicationId: selectedApplication?.id,
  });

  const {
    data: fetchedEnvironmentParameters,
    error: errorFetchingEnvironmentParameters,
  } = useDeploymentEnvironmentParameters({
    latest: true,
    projectId: openProjectData.id,
    applicationId: selectedApplication?.id,
  });

  const {
    data: defaultAnalyticParameters = [],
    error: errorFetchingDefaultAnalyticParameters,
  } = useAnalyticsParameters({
    avoidFetch: !isItAnAnalyticsApplication,
    projectId: openProjectData.id,
    applicationId: selectedApplication?.id,
  });

  const {
    data: fetchedAnalyticParameters,
    error: errorFetchingAnalyticParameters,
  } = useDeploymentAnalyticParameters({
    latest: true,
    projectId: openProjectData.id,
    applicationId: selectedApplication?.id,
    avoidFetch: !isItAnAnalyticsApplication,
  });

  useEffect(() => {
    if (errorFetchingAnalyticParameters || errorFetchingDefaultAnalyticParameters) {
      toast.error(
        intl.formatMessage({
          id: 'analyticsParameters.errorGettingAnalyticsParameters',
        })
      );
    }
  }, [intl, errorFetchingAnalyticParameters, errorFetchingDefaultAnalyticParameters]);

  useEffect(() => {
    if (errorFetchingEnvironmentParameters || errorFetchingDefaultEnvironmentParameters) {
      toast.error(
        intl.formatMessage({
          id: 'environmentParameters.errorGettingEnvironmentParameters',
        })
      );
    }
  }, [
    intl,
    errorFetchingEnvironmentParameters,
    errorFetchingDefaultEnvironmentParameters,
  ]);

  return (
    <section className="deploy-settings-container">
      <AnalyticsParameters
        historyItems={historyItems}
        isTaskInProgress={isTaskInProgress}
        selectedApplication={selectedApplication}
        testSelectedDeployment={testSelectedDeployment}
        defaultAnalyticParameters={defaultAnalyticParameters}
        fetchedAnalyticParameters={fetchedAnalyticParameters}
        isItAnAnalyticsApplication={isItAnAnalyticsApplication}
      />
      <EnvironmentParameters
        historyItems={historyItems}
        isTaskInProgress={isTaskInProgress}
        selectedApplication={selectedApplication}
        testSelectedDeployment={testSelectedDeployment}
        defaultEnvironmentParameters={defaultEnvironmentParameters}
        fetchedEnvironmentParameters={fetchedEnvironmentParameters}
      />
    </section>
  );
}

const mapStateToProps = (state: RootState) => ({
  openProjectData: getProjectData(state),
});

export const DeploySettings = connect(mapStateToProps)(DeploySettingsBase);
